import React, { useRef } from 'react';
import { Platform, Pressable, View } from 'react-native';

import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const NEXT_OPTION_KEYS = new Set(['ArrowRight', 'ArrowDown', 'Right', 'Down']);
const PREVIOUS_OPTION_KEYS = new Set(['ArrowLeft', 'ArrowUp', 'Left', 'Up']);

/**
 * YesNo: two-option segmented control for checklist questions.
 * Props:
 * - value: true (yes) | false (no) | null/undefined (unanswered)
 * - onChange(nextValue: boolean)
 * - yesLabel (default 'Yes'), noLabel (default 'No'; e.g. 'Not needed', 'N/A')
 * - label, hint: when given, renders the whole question row (text left, control right)
 * - accessibilityLabel: the question for screen readers when it is drawn elsewhere (defaults to label)
 * - disabled, style
 */
const YesNo = ({
	value,
	onChange,
	yesLabel = 'Yes',
	noLabel = 'No',
	label,
	hint,
	accessibilityLabel = label,
	disabled = false,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const optionRefs = useRef([]);
	const options = [
		{ key: 'yes', optionValue: true, text: yesLabel },
		{ key: 'no', optionValue: false, text: noLabel },
	];
	const hasAnswer = options.some(({ optionValue }) => value === optionValue);

	// react-native-web only activates role="radio" with Enter. A radio group also
	// selects the focused option with Space and moves the selection (and focus)
	// with the arrow keys; only the selected option (or the first) is in the tab order.
	const getWebKeyProps = (index, isSelected) => {
		if (Platform.OS !== 'web' || disabled) {
			return {};
		}

		return {
			tabIndex: isSelected || (!hasAnswer && index === 0) ? 0 : -1,
			onKeyDown: (event) => {
				if (event.key === ' ' || event.key === 'Spacebar') {
					event.preventDefault();
					if (!event.repeat) {
						onChange?.(options[index].optionValue);
					}
					return;
				}

				const isNext = NEXT_OPTION_KEYS.has(event.key);
				if (!isNext && !PREVIOUS_OPTION_KEYS.has(event.key)) {
					return;
				}

				event.preventDefault();
				const nextIndex = (index + (isNext ? 1 : -1) + options.length) % options.length;
				onChange?.(options[nextIndex].optionValue);
				optionRefs.current[nextIndex]?.focus?.();
			},
		};
	};

	const control = (
		<View style={[styles.control, disabled && styles.controlDisabled]} accessibilityRole="radiogroup" accessibilityLabel={accessibilityLabel}>
			{options.map(({ key, optionValue, text }, index) => {
				const isSelected = value === optionValue;
				return (
					<Pressable
						key={key}
						ref={(node) => {
							optionRefs.current[index] = node;
						}}
						onPress={disabled ? undefined : () => onChange?.(optionValue)}
						{...getWebKeyProps(index, isSelected)}
						disabled={disabled}
						accessibilityRole="radio"
						accessibilityLabel={accessibilityLabel ? `${accessibilityLabel}: ${text}` : text}
						accessibilityState={{ selected: isSelected, checked: isSelected, disabled }}
						// react-native-web does not turn accessibilityState into aria-checked.
						aria-checked={isSelected}
						style={[styles.option, isSelected && styles.optionSelected]}
					>
						<Text style={[styles.optionText, isSelected && styles.optionTextSelected]} numberOfLines={1}>
							{text}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);

	if (!label) {
		return <View style={style}>{control}</View>;
	}

	return (
		<View style={[styles.row, style]}>
			<View style={styles.question}>
				<Text style={styles.label}>{label}</Text>
				{Boolean(hint) && <Text style={styles.hint}>{hint}</Text>}
			</View>
			{control}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, type, space, touch } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 64,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	question: {
		flex: 1,
		gap: 3,
	},
	label: {
		...type.rowTitle,
		color: colors.text,
	},
	hint: {
		...type.caption,
		color: colors.textSecondary,
	},
	control: {
		flexDirection: 'row',
		gap: 2,
		padding: 3,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
		flexShrink: 0,
	},
	controlDisabled: {
		opacity: 0.6,
	},
	option: {
		minWidth: 44,
		height: touch.min,
		paddingHorizontal: 10,
		borderRadius: radius.pill,
		alignItems: 'center',
		justifyContent: 'center',
	},
	optionSelected: {
		backgroundColor: colors.primary,
	},
	optionText: {
		...type.secondaryStrong,
		color: colors.icon,
	},
	optionTextSelected: {
		color: colors.onPrimary,
	},
});

export default YesNo;
